import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Typography,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import {
  SnackbarApiErrorMessage,
  useLocalization,
  useSnackbar,
  useStore,
} from '@ui-framework/index';
import {
  activeViewColumnsOnly,
  buildDialogOrderFromConfig,
  extractViewColumns,
  extractViewConfigMeta,
  flattenCatalogItems,
  getColumnId,
  getOrderingLockedPrefixLength,
  isColumnOrderingLocked,
  orderSchemaByColumnIds,
  pinColumnOrder,
} from '@ui-framework/components/Forms/lib/listCatalogColumns';
import {
  CatalogPickerDropLine,
  CatalogPickerRow,
  useCatalogDragReorder,
} from '@ui-framework/components/Dialogs/CatalogPickerParts';
import ThemedJoyrideTour from '@ui-framework/components/Dialogs/ThemedJoyrideTour';
import useCatalogPickerTour from '@ui-framework/components/Dialogs/useCatalogPickerTour';
import DialogCloseButton from '@product-framework/components/Dialogs/DialogCloseButton';
import { buildColumnPickerTourSteps } from './buildColumnPickerTourSteps';

function getColumnLabel(item, formatMessage) {
  const headerSource = item?.header || item?.label;
  if (headerSource?.localeId || headerSource?.defaultMessage) {
    return formatMessage({
      id: headerSource.localeId || item.name,
      defaultMessage:
        headerSource.defaultMessage || item.title || item.name || '',
    });
  }
  return item?.title || item?.name || item?.column || item?.accessorKey || '';
}

/**
 * Fetches layout catalog on open; saves ordered subset via PUT layout/config.
 */
function ShowHideColumnsDialog({
  open,
  onClose,
  onSaved,
  dataProvider,
  storeName,
  catalogApiUrl,
  configApiUrl,
}) {
  const { formatMessage } = useLocalization();
  const snackbar = useSnackbar();
  const store = useStore();
  const { get, put } = dataProvider;

  const [catalogItems, setCatalogItems] = useState([]);
  const [orderedIds, setOrderedIds] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [, setOrderTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { tourRun, startTour, joyrideProps, disableEnforceFocus } =
    useCatalogPickerTour({ resetWhen: !open });
  const tourSteps = useMemo(
    () => buildColumnPickerTourSteps(formatMessage),
    [formatMessage],
  );

  // Observe schema / viewConfig so Save uses latest after 409 refresh.
  const viewConfig = store.domain.list.viewConfigs?.[storeName] ?? null;

  const catalogById = useMemo(() => {
    const map = new Map();
    catalogItems.forEach(item => {
      const id = getColumnId(item);
      if (id) {
        map.set(id, item);
      }
    });
    return map;
  }, [catalogItems]);

  const catalogIds = useMemo(
    () => catalogItems.map(getColumnId).filter(Boolean),
    [catalogItems],
  );

  const lockedIds = useMemo(
    () =>
      catalogItems
        .filter(item => item.enableHiding === false)
        .map(getColumnId)
        .filter(Boolean),
    [catalogItems],
  );

  const orderingLockedPrefixLength = useMemo(
    () => getOrderingLockedPrefixLength(catalogItems),
    [catalogItems],
  );

  const toggleableIds = useMemo(
    () => catalogIds.filter(id => !lockedIds.includes(id)),
    [catalogIds, lockedIds],
  );

  const firstTourRowIndex = useMemo(
    () =>
      orderedIds.findIndex(id => {
        const item = catalogById.get(id);
        return item && !isColumnOrderingLocked(item);
      }),
    [orderedIds, catalogById],
  );

  const canDragFromIndex = useCallback(
    index => {
      const fromId = orderedIds[index];
      return !isColumnOrderingLocked(catalogById.get(fromId));
    },
    [orderedIds, catalogById],
  );

  const {
    dragIndex,
    dropAt,
    clearDragState,
    resetDragState,
    handleDragStart,
    handleRowDragOver,
    handleListDragOver,
    handleRowDrop,
    handleMove,
  } = useCatalogDragReorder({
    orderedIds,
    setOrderedIds,
    setOrderTouched,
    saving,
    minDropIndex: orderingLockedPrefixLength,
    canDragFromIndex,
    canDropAtIndex: index => index >= orderingLockedPrefixLength,
  });

  const allSelected =
    catalogIds.length > 0 &&
    catalogIds.every(id => lockedIds.includes(id) || selectedIds.includes(id));

  const someSelected =
    !allSelected &&
    catalogIds.some(id => lockedIds.includes(id) || selectedIds.includes(id));

  const handleSelectAll = checked => {
    if (checked) {
      setSelectedIds(catalogIds);
      return;
    }
    if (lockedIds.length === 0) {
      snackbar.showMessage({
        type: 'error',
        data: (
          <SnackbarApiErrorMessage
            errors={[
              {
                message: formatMessage({
                  id: 'SHOW_HIDE_COLUMNS_MIN_ONE',
                  defaultMessage: 'Select at least one column.',
                }),
              },
            ]}
          />
        ),
      });
      return;
    }
    setSelectedIds(lockedIds);
  };

  useEffect(() => {
    if (!open || !catalogApiUrl || !configApiUrl) {
      return undefined;
    }

    let cancelled = false;

    const loadCatalog = async () => {
      setLoading(true);
      try {
        const [catalogResponse, configResponse] = await Promise.all([
          get(catalogApiUrl),
          get(configApiUrl),
        ]);
        if (cancelled) {
          return;
        }
        if (!catalogResponse?.ok) {
          snackbar.showMessage({
            type: 'error',
            data: <SnackbarApiErrorMessage errors={catalogResponse?.errors} />,
          });
          return;
        }
        const flattened = flattenCatalogItems(
          extractViewColumns(catalogResponse.data),
        ).filter(item => getColumnId(item));
        setCatalogItems(flattened);

        const nextCatalogIds = flattened.map(getColumnId).filter(Boolean);
        const hasSavedConfig = configResponse?.ok;
        const configColumns = hasSavedConfig
          ? extractViewColumns(configResponse.data).filter(
              item =>
                getColumnId(item) && nextCatalogIds.includes(getColumnId(item)),
            )
          : [];
        // Full preference order (active + hidden) for dialog reorder.
        const configIds = configColumns.map(getColumnId).filter(Boolean);
        // Checked boxes = active preference columns only.
        const configActiveIds = activeViewColumnsOnly(configColumns)
          .map(getColumnId)
          .filter(Boolean);

        const savedVisibleIds = hasSavedConfig
          ? configActiveIds
          : (store.domain.list.getSchema(storeName) || [])
              .map(getColumnId)
              .filter(id => id && nextCatalogIds.includes(id));

        setSelectedIds(savedVisibleIds);
        setOrderTouched(false);
        // Saved config: dialog matches config API order; no config yet: catalog order.
        setOrderedIds(
          hasSavedConfig && configIds.length > 0
            ? buildDialogOrderFromConfig({
                catalogIds: nextCatalogIds,
                configIds,
              })
            : nextCatalogIds,
        );

        if (hasSavedConfig) {
          let nextSchema = activeViewColumnsOnly(configColumns);
          if (
            configActiveIds.length > 0 &&
            Array.isArray(nextSchema) &&
            nextSchema.length > 0
          ) {
            nextSchema = orderSchemaByColumnIds(nextSchema, configActiveIds);
          }
          if (Array.isArray(nextSchema) && nextSchema.length > 0) {
            store.domain.list.setSchema(storeName, nextSchema);
          }
          store.domain.list.setViewConfig(
            storeName,
            extractViewConfigMeta(configResponse.data),
          );
        }
      } catch (error) {
        if (!cancelled) {
          snackbar.showMessage({
            type: 'error',
            data: (
              <SnackbarApiErrorMessage
                errors={[
                  { message: error?.message || 'Failed to load catalog' },
                ]}
              />
            ),
          });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadCatalog();

    return () => {
      cancelled = true;
    };
  }, [open, catalogApiUrl, configApiUrl, get, snackbar, store, storeName]);

  useEffect(() => {
    if (!open) {
      setCatalogItems([]);
      setOrderedIds([]);
      setSelectedIds([]);
      setOrderTouched(false);
      resetDragState();
    }
  }, [open, resetDragState]);

  const handleResetToCatalog = () => {
    clearDragState();
    setOrderTouched(true);
    setOrderedIds(catalogIds);
    setSelectedIds(catalogIds);
  };

  const refreshConfigSchema = useCallback(
    async (preferredColumnOrder = []) => {
      const response = await get(configApiUrl);
      if (!response?.ok) {
        return response;
      }
      let nextSchema = activeViewColumnsOnly(extractViewColumns(response.data));
      if (
        preferredColumnOrder.length > 0 &&
        Array.isArray(nextSchema) &&
        nextSchema.length > 0
      ) {
        nextSchema = orderSchemaByColumnIds(nextSchema, preferredColumnOrder);
      }
      if (Array.isArray(nextSchema) && nextSchema.length > 0) {
        store.domain.list.setSchema(storeName, nextSchema);
      }
      store.domain.list.setViewConfig(
        storeName,
        extractViewConfigMeta(response.data),
      );
      return response;
    },
    [configApiUrl, get, store, storeName],
  );

  const handleToggle = (id, locked) => {
    if (locked) {
      return;
    }
    setSelectedIds(prev => {
      if (prev.includes(id)) {
        const remaining = new Set([
          ...prev.filter(x => x !== id),
          ...lockedIds,
        ]);
        if (remaining.size === 0) {
          snackbar.showMessage({
            type: 'error',
            data: (
              <SnackbarApiErrorMessage
                errors={[
                  {
                    message: formatMessage({
                      id: 'SHOW_HIDE_COLUMNS_MIN_ONE',
                      defaultMessage: 'Select at least one column.',
                    }),
                  },
                ]}
              />
            ),
          });
          return prev;
        }
        return prev.filter(x => x !== id);
      }
      return [...prev, id];
    });
  };

  const handleSave = async () => {
    const selected = new Set([...selectedIds, ...lockedIds]);
    // Full dialog order wins; unchecked stay in place with active:false.
    const orderedFull = pinColumnOrder(orderedIds, catalogItems);
    const orderedSelected = orderedFull.filter(id => selected.has(id));

    if (orderedSelected.length === 0) {
      snackbar.showMessage({
        type: 'error',
        data: (
          <SnackbarApiErrorMessage
            errors={[
              {
                message: formatMessage({
                  id: 'SHOW_HIDE_COLUMNS_MIN_ONE',
                  defaultMessage: 'Select at least one column.',
                }),
              },
            ]}
          />
        ),
      });
      return;
    }

    const recordVersion = viewConfig?.recordVersion ?? 1;
    const sort = viewConfig?.sort;

    setSaving(true);
    try {
      const putResponse = await put(configApiUrl, {
        config: {
          columns: orderedFull.map(name => ({
            name,
            active: selected.has(name),
          })),
          ...(sort !== undefined ? { sort } : {}),
        },
        recordVersion,
      });

      if (!putResponse?.ok) {
        const statusCode = putResponse?.statusCode;
        if (statusCode === 409) {
          await refreshConfigSchema();
          snackbar.showMessage({
            type: 'error',
            data: (
              <SnackbarApiErrorMessage
                errors={
                  putResponse?.errors || [
                    {
                      message: formatMessage({
                        id: 'SHOW_HIDE_COLUMNS_STALE_VERSION',
                        defaultMessage:
                          'Column settings were updated elsewhere. Try saving again.',
                      }),
                    },
                  ]
                }
              />
            ),
          });
          return;
        }
        snackbar.showMessage({
          type: 'error',
          data: <SnackbarApiErrorMessage errors={putResponse?.errors} />,
        });
        return;
      }

      const refreshResponse = await refreshConfigSchema(orderedSelected);
      if (!refreshResponse?.ok) {
        snackbar.showMessage({
          type: 'error',
          data: <SnackbarApiErrorMessage errors={refreshResponse?.errors} />,
        });
        return;
      }

      if (typeof onSaved === 'function') {
        await onSaved(putResponse, { preferredColumnOrder: orderedSelected });
      }
      onClose();
    } catch (error) {
      snackbar.showMessage({
        type: 'error',
        data: (
          <SnackbarApiErrorMessage
            errors={[{ message: error?.message || 'Failed to save columns' }]}
          />
        ),
      });
    } finally {
      setSaving(false);
    }
  };

  const renderDialogBody = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={32} />
        </Box>
      );
    }
    if (catalogItems.length === 0) {
      return (
        <Typography variant="body2">
          {formatMessage({
            id: 'SHOW_HIDE_COLUMNS_EMPTY',
            defaultMessage: 'No columns available.',
          })}
        </Typography>
      );
    }
    return (
      <FormGroup>
        <Box data-tour="column-select-all">
          <FormControlLabel
            control={
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected}
                disabled={saving || toggleableIds.length === 0}
                onChange={event => handleSelectAll(event.target.checked)}
              />
            }
            label={formatMessage({
              id: 'SHOW_HIDE_COLUMNS_SELECT_ALL',
              defaultMessage: 'Select All',
            })}
            sx={{ mb: 1, fontWeight: 600 }}
          />
        </Box>
        {orderedIds.map((id, index) => {
          const item = catalogById.get(id);
          if (!item) {
            return null;
          }
          const hidingLocked = item.enableHiding === false;
          const orderingLocked = isColumnOrderingLocked(item);
          const checked = hidingLocked || selectedIds.includes(id);
          const visibleCount = new Set([...selectedIds, ...lockedIds]).size;
          const isLastSelected = checked && !hidingLocked && visibleCount === 1;
          const canReorder = !saving && !orderingLocked;
          const canMoveUp = canReorder && index > orderingLockedPrefixLength;
          const canMoveDown = canReorder && index < orderedIds.length - 1;
          const isTourRow = index === firstTourRowIndex;
          return (
            <CatalogPickerRow
              key={id}
              id={id}
              index={index}
              label={getColumnLabel(item, formatMessage)}
              checked={checked}
              checkboxDisabled={hidingLocked || saving || isLastSelected}
              onToggle={() => handleToggle(id, hidingLocked)}
              isDragging={dragIndex === index}
              showLineBefore={dragIndex !== null && dropAt === index}
              canReorder={canReorder}
              canMoveUp={canMoveUp}
              canMoveDown={canMoveDown}
              showMoveButtons={!orderingLocked}
              dragHandleHidden={orderingLocked}
              onDragStart={handleDragStart}
              onDragEnd={clearDragState}
              onRowDragOver={handleRowDragOver}
              onRowDrop={handleRowDrop}
              onMoveUp={() => handleMove(index, index - 1)}
              onMoveDown={() => handleMove(index, index + 1)}
              moveUpLabel={formatMessage({
                id: 'SHOW_HIDE_COLUMNS_MOVE_UP',
                defaultMessage: 'Move up',
              })}
              moveDownLabel={formatMessage({
                id: 'SHOW_HIDE_COLUMNS_MOVE_DOWN',
                defaultMessage: 'Move down',
              })}
              tourAnchors={
                isTourRow
                  ? {
                      row: 'column-first-row',
                      drag: 'column-first-drag',
                      arrows: 'column-first-arrows',
                    }
                  : undefined
              }
            />
          );
        })}
        {dragIndex !== null && dropAt === orderedIds.length && (
          <CatalogPickerDropLine />
        )}
        <Box
          role="presentation"
          aria-hidden
          onDragOver={handleListDragOver}
          onDrop={handleRowDrop}
          sx={{ minHeight: 12 }}
        />
      </FormGroup>
    );
  };

  const tourButtonLabel = formatMessage({
    id: 'SHOW_HIDE_COLUMNS_TOUR_BUTTON',
    defaultMessage: 'How it works',
  });

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      disableEnforceFocus={disableEnforceFocus}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ViewColumnIcon />
        {formatMessage({
          id: 'SHOW_HIDE_COLUMNS_TITLE',
          defaultMessage: 'Show / Hide Columns',
        })}
        <Button
          size="small"
          variant="text"
          startIcon={<HelpOutlineIcon />}
          onClick={startTour}
          disabled={loading || saving || catalogItems.length === 0 || tourRun}
          aria-label={tourButtonLabel}
          sx={{ ml: 'auto', mr: 4 }}>
          {tourButtonLabel}
        </Button>
        <DialogCloseButton disabled={saving} onClose={onClose} />
      </DialogTitle>
      <DialogContent>{renderDialogBody()}</DialogContent>
      <DialogActions
        sx={{ justifyContent: 'center', my: 1, gap: 1, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading || saving || catalogItems.length === 0}
          data-tour="column-save">
          {saving
            ? formatMessage({
                id: 'SAVING_BUTTON',
                defaultMessage: 'Saving…',
              })
            : formatMessage({
                id: 'SAVE_BUTTON',
                defaultMessage: 'Save',
              })}
        </Button>
        <Button
          variant="outlined"
          onClick={handleResetToCatalog}
          disabled={loading || saving || catalogItems.length === 0}
          data-tour="column-reset">
          {formatMessage({
            id: 'SHOW_HIDE_COLUMNS_RESET_DEFAULT',
            defaultMessage: 'Reset to default',
          })}
        </Button>
        <Button variant="outlined" onClick={onClose} disabled={saving}>
          {formatMessage({
            id: 'CANCEL_BUTTON',
            defaultMessage: 'Cancel',
          })}
        </Button>
      </DialogActions>
      <ThemedJoyrideTour
        {...joyrideProps}
        steps={tourSteps}
        formatMessage={formatMessage}
      />
    </Dialog>
  );
}

export default ShowHideColumnsDialog;
